import type { FileRecord, FileService } from "@/shared/domain/files";
import {
  type MessagePathRepresentationMapping,
  RepresentationRecord,
} from "@/shared/domain/topics";
import { LocalFile, RemoteFile } from "@/shared/fs";

import { type ReaderLike, McapReader, zipMessages } from "./mcap";
import type { Message } from "./Message";
import {
  type TopicReader,
  type IterLogTimeBatchesOptions,
  type IterMessagesOptions,
  type LogTimeIndexEntry,
  TopicReaderEventTarget,
} from "./TopicReader";

// prettier-ignore
export class McapTopicReader extends TopicReaderEventTarget implements TopicReader {
  public readonly topicId: string;

  private cachedFileToReaderMap: Map<string, McapReader>;
  private fileService: FileService;
  private messagePathToRepresentationMaps: MessagePathRepresentationMapping[];
  private localFileCache: Map<string, LocalFile>;
  private representationFileRecordCache: Map<string, FileRecord>;

  constructor(
    topicId: string,
    messagePathToRepresentationMaps: MessagePathRepresentationMapping[],
    fileService: FileService,
  ) {
    super();
    this.topicId = topicId;

    this.cachedFileToReaderMap = new Map();
    this.fileService = fileService;
    this.messagePathToRepresentationMaps = messagePathToRepresentationMaps;
    this.localFileCache = new Map();
    this.representationFileRecordCache = new Map();
  }

  dispose(): void {
    this.removeAllListeners();
    this.cachedFileToReaderMap.clear();
    this.localFileCache.clear();
    this.representationFileRecordCache.clear();
  }

  async *iterLogTimeBatches({
    filter,
    signal,
  }: IterLogTimeBatchesOptions): AsyncGenerator<LogTimeIndexEntry[], void, undefined> {
    // GM(2025-05):
    // Heuristic: a topic has a single time index, which means that all representations will have the same log times.
    // Extract log time index from the first representation.
    // In many cases, topics stored in MCAP files will have all MessagePaths in the same MCAP file.
    // In those cases, the repr file downloaded and used here will be the same as the one used to iter messages.
    // Topics which use per-MessagePath MCAP files, however, will incur inefficiency in this implementation.
    // It would be better to identify a repr that holds MessagePath data that we'll need as part of iterMessages.
    const anyMessagePathId = this.messagePathToRepresentationMaps
      .at(0)
      ?.message_paths.at(0)?.message_path_id;
    if (anyMessagePathId === undefined) {
      throw new Error(`Failed to read data topic ${this.topicId}`, {
        cause:
          "This Topic does not have any mappings between MessagePaths and Representations",
      });
    }
    const readers = await this.collectReadersForMessagePaths(
      new Set([anyMessagePathId]),
      signal,
    );
    const reader = readers[0];
    yield* reader.iterMessageChunksIndices({ filter, abortSignal: signal });
  }

  async *iterMessages({
    filter,
    messagePathsInclude,
    signal,
  }: IterMessagesOptions): AsyncGenerator<Message, void, undefined> {
    const readers = await this.collectReadersForMessagePaths(
      new Set(messagePathsInclude),
      signal,
    );
    for await (const message of zipMessages(readers, {
      filter,
      signal,
    })) {
      yield message;
    }
  }

  /**
   * Returns the LocalFile handle for the OPFS-cached copy of a representation's MCAP file,
   * creating it on first access and memoizing it per representation. The on-disk path is:
   *
   *   $OPFSROOT / <topic_id> / <representation_id> / <associated_file_id>.<storage_format>
   *
   * The returned handle does not guarantee the bytes exist on disk; callers must check
   * with `isCached` before reading.
   */
  private cacheFileForRepresentation(
    representation: RepresentationRecord,
  ): LocalFile {
    const reprCacheKey = representation.association.association_id;
    if (this.localFileCache.has(reprCacheKey)) {
      return this.localFileCache.get(reprCacheKey)!;
    }
    const file = new LocalFile({
      parents: [this.topicId, representation.representation_id],
      name: `${representation.association.association_id}.${representation.storage_format.toLowerCase()}`,
    });
    this.localFileCache.set(reprCacheKey, file);
    return file;
  }

  private async collectReadersForMessagePaths(
    messagePathIds: Set<string>,
    signal?: AbortSignal,
  ): Promise<ReaderLike[]> {
    // An empty request is a no-op: the caller asked for zero message paths,
    // so zero readers is the correct answer. This is different from passing
    // message path ids that don't map to any representation, which is treated
    // as a configuration error and throws below (see the readers.length === 0
    // and remaining.size > 0 checks at the end of this method).
    if (messagePathIds.size === 0) {
      return [];
    }

    const remaining = new Set(messagePathIds);
    const readers: ReaderLike[] = [];
    for (const mapping of this.messagePathToRepresentationMaps) {
      const relevant = mapping.message_paths.filter((record) =>
        messagePathIds.has(record.message_path_id),
      );
      if (relevant.length === 0) {
        continue;
      }

      const representationFileRecord =
        await this.getRepresentationAssociatedFileRecord(
          mapping.representation,
          signal,
        );

      const local = this.cacheFileForRepresentation(mapping.representation);
      const cacheKey = local.id();
      // Serialize cache checks and downloads for this representation across every
      // McapTopicReader instance in the tab. Without this lock, two readers asked for
      // the same topic at once would both see "not cached" and race to download the
      // same MCAP file into OPFS.
      await navigator.locks.request(
        `collectReadersForMessagePaths#${cacheKey}`,
        { signal },
        async () => {
          signal?.throwIfAborted();

          const isCached = await this.isCached(
            local,
            representationFileRecord.size,
          );
          if (this.cachedFileToReaderMap.has(cacheKey)) {
            if (!isCached) {
              // OPFS evicted the bytes (e.g. storage pressure) since the reader was
              // built. Drop the stale reader so the fetch path below rebuilds it.
              this.cachedFileToReaderMap.delete(cacheKey);
            } else {
              const reader = this.cachedFileToReaderMap.get(cacheKey)!;
              relevant.forEach((mp) => {
                remaining.delete(mp.message_path_id);
              });
              readers.push(reader);
              return;
            }
          }

          if (!isCached) {
            // Bytes are not in OPFS — fetch from S3. The downloadstart/downloadend
            // pair lets UI components show a loading indicator for this representation;
            // `priority: "foreground"` flags it as user-visible work (the user is
            // actively waiting on this topic's data).
            const loadingId = Symbol.for(cacheKey);
            this.dispatchEvent(
              new CustomEvent("downloadstart", {
                detail: { id: loadingId, priority: "foreground" },
              }),
            );
            const remote = new RemoteFile(
              representationFileRecord,
              this.fileService,
            );
            await remote.toLocalFile({
              path: local,
              signal,
            });
            signal?.throwIfAborted();
            this.dispatchEvent(
              new CustomEvent("downloadend", { detail: { id: loadingId } }),
            );
          }

          // Bytes are in OPFS at this point (either preexisting or just downloaded),
          // but no McapReader has been built around them yet for this instance.
          const reader = await McapReader.forFile(local, {
            abortSignal: signal,
            cache: false,
          });
          this.cachedFileToReaderMap.set(cacheKey, reader);
          relevant.forEach((mp) => {
            remaining.delete(mp.message_path_id);
          });
          readers.push(reader);
        },
      );
    }

    if (readers.length === 0) {
      throw new Error(`Failed to read data topic ${this.topicId}`, {
        cause: `Message paths ${Array.from(messagePathIds).join(", ")} not mapped to any representations`,
      });
    }

    if (remaining.size > 0) {
      throw new Error(
        [
          `Failed to construct a reader for Topic '${this.topicId}'.`,
          "Could not determine where to find data for the following MessagePaths:",
          JSON.stringify(Array.from(remaining)),
        ].join(" "),
      );
    }

    return readers;
  }

  private async getRepresentationAssociatedFileRecord(
    representation: RepresentationRecord,
    signal?: AbortSignal,
  ): Promise<FileRecord> {
    // Memoize per representation so a single iter call doesn't re-fetch the same
    // FileRecord once for every message path that maps to it.
    const cacheKey = representation.representation_id;
    if (this.representationFileRecordCache.has(cacheKey)) {
      return this.representationFileRecordCache.get(cacheKey)!;
    } else {
      const record = await this.fileService.getFileRecord(
        representation.association.association_id,
        { abortSignal: signal },
      );
      this.representationFileRecordCache.set(cacheKey, record);
      return record;
    }
  }

  private async isCached(file: LocalFile, expectedSize: number) {
    return await file.exists(expectedSize);
  }
}
